import { Box, HStack, VStack } from "@chakra-ui/layout";
import { Text, Button, useToast, Flex } from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { LoggedInLayout } from "../components/layouts/LoggedInLayout";
import { useCreateJob, useCustomer } from "@api/hooks";
import TabItem from "../components/tab/TabItem";
import { BookingType, StepOneData, StepThreeData, StepTwoData, TabItemStatus } from "@models/Order";
import { useRef, useState } from "react";
import RightArrow from "../components/icons/RightArrow";
import StepOne from "../components/createOrder/StepOne";
import StepTwo from "../components/createOrder/StepTwo";
import StepThree from "../components/createOrder/StepThree";
import LoadingIndicator from "../components/Loading";
import { getErrorInJobCreation } from "@utils/utils";
import moment from "moment";

const tabListData = [
	{
		id: "1",
		title: "Uppdrag",
		isActive: true,
		status: TabItemStatus.Todo,
	},
	{
		id: "2",
		title: "Tid",
		isActive: false,
		status: TabItemStatus.Todo,
	},
	{
		id: "3",
		title: "Bekräfta",
		isActive: false,
		status: TabItemStatus.Todo,
	},
];

const JobCreatePage = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const toast = useToast();
	const {
		mutate: createJobMutation,
		isLoading: jobCreationLoading,
		isError: isErrorJobCreation,
	} = useCreateJob(undefined, (code) => {
		toast({
			title: getErrorInJobCreation(code),
			status: "error",
			duration: 9000,
			isClosable: true,
			variant: "subtle",
		});
	});
	const stepOneRef = useRef<any>(null);
	const stepTwoRef = useRef<any>(null);

	const customerId = searchParams.get("customer") || "";

	const [stepOneData, setStepOneData] = useState<StepOneData>({
		competence: { branch: "", service: "", skills: [] },
	});
	const [stepTwoData, setStepTwoData] = useState<StepTwoData>({ shifts: [] });
	const [stepThreeData, setStepThreeData] = useState<StepThreeData>({
		bookingType: BookingType.OPEN,
		customer: { id: customerId },
		numPositions: 1,
	});
	const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(true);
	const [tabData, setTabData] = useState(tabListData);

	const { customer, isLoading } = useCustomer(customerId);

	const activeStepId = Number(tabData.find((tab) => tab.isActive)?.id);

	const handleTabItemClick = (id: number) => {
		!isNextButtonDisabled && handleNextButtonClick();
		setTabData((prev) => {
			return prev.map((tab) => {
				if (Number(tab.id) === id) {
					return {
						...tab,
						isActive: true,
					};
				}
				return {
					...tab,
					isActive: false,
				};
			});
		});
	};

	const handleNextButtonClick = () => {
		if (activeStepId === 1) {
			stepOneRef.current?.onConfirmData();
		} else if (activeStepId === 2) {
			stepTwoRef.current?.onConfirmData();
		}

		setTabData((prev) => {
			const activeTabId = Number(prev.find((tab) => tab.isActive)!.id);
			if (activeTabId + 1 <= tabData.length) {
				return prev.map((tab) => {
					if (Number(tab.id) === activeTabId + 1) {
						return {
							...tab,
							isActive: true,
						};
					}
					if (Number(tab.id) < activeTabId + 1) {
						return {
							...tab,
							isActive: false,
							status: TabItemStatus.Completed,
						};
					}
					return {
						...tab,
						isActive: false,
					};
				});
			}
			return prev;
		});

		setIsNextButtonDisabled(true);
	};

	const handleSubmit = async (data: StepThreeData) => {
		try {
			const body: any = {
				...stepOneData,
				contactPersons: stepOneData.contactPersons?.map((cp) => ({
					id: cp.id,
					firstName: cp?.firstName,
					lastName: cp?.lastName,
					email: cp?.email,
					profile: { phoneNumber: cp?.profile?.phoneNumber },
				})),
				shifts: stepTwoData.shifts.map(({ start, end, break: breakVal, flexible }) => ({
					start: moment(start).toISOString(),
					end: moment(end).toISOString(),
					break: breakVal,
					flexible: !!flexible ? true : undefined,
				})),
				...data,
			};

			if (!stepOneData.competence.service.length) {
				delete body.competence.skills;
			}
			if (stepOneData.competence.skills && stepOneData.competence.skills.length === 0) {
				delete body.competence.skills;
			}
			if (data.invitations && data.invitations.length === 0) {
				delete body.invitations;
			}
			await createJobMutation(body);
		} catch (err) {
			toast({
				title: "Order creation failed please try again.",
				status: "error",
				description: JSON.stringify(err),
				duration: 9000,
				isClosable: true,
				variant: "subtle",
			});
		}
	};

	function renderNextButton() {
		if (activeStepId !== 3) {
			return (
				<Button
					variant="solid"
					height="40px"
					borderRadius={"20px"}
					width={"145px"}
					color="white"
					bg={"#2A2A2A"}
					_hover={{
						backgroundColor: isNextButtonDisabled ? "#9fa0a3" : "#2A2A2A",
					}}
					onClick={handleNextButtonClick}
					rightIcon={<RightArrow />}
					isDisabled={isNextButtonDisabled}
				>
					Nästa
				</Button>
			);
		}
		return null;
	}

	return (
		<LoggedInLayout width="l" bg={"#edeff3"}>
			<Styles>
				<HStack justifyContent="space-between">
					<VStack alignItems="start">
						<Text fontWeight={600} fontSize={24} mt={2} mb={0}>
							Skapa uppdrag
						</Text>
						<Text fontWeight={400} fontSize={14} color={"#35353580"} my={0}>
							{customer?.name}
						</Text>
					</VStack>
					{renderNextButton()}
				</HStack>

				<HStack mt={4}>
					{tabData.map(({ id, title, status, isActive }) => (
						<TabItem
							onClick={handleTabItemClick}
							id={id}
							key={id}
							width={`${Math.trunc(100 / tabListData.length)}%`}
							title={title}
							status={status}
							isActive={isActive}
							isNextButtonDisabled={isNextButtonDisabled}
							activeStepId={activeStepId}
						/>
					))}
				</HStack>
				<Box borderRadius={"lg"} backgroundColor={"white"} p={7} mt={3} mb={3}>
					{!isLoading && activeStepId === 1 && (
						<StepOne
							customer={customer}
							loadingCustomerData={isLoading}
							ref={stepOneRef}
							data={stepOneData}
							onChangeData={(data) => setStepOneData(data)}
							toggleNextButtonDisable={(status) => setIsNextButtonDisabled(status)}
						/>
					)}
					{!isLoading && activeStepId === 2 && (
						<StepTwo
							ref={stepTwoRef}
							data={stepTwoData}
							onChangeData={(data) => setStepTwoData(data)}
							toggleNextButtonDisable={(status) => setIsNextButtonDisabled(status)}
						/>
					)}
					{!isLoading && activeStepId === 3 && (
						<StepThree
							customer={customer}
							loadingCustomerData={isLoading}
							jobCreationLoading={jobCreationLoading}
							hasErrorInJobCreation={isErrorJobCreation}
							stepOneData={stepOneData}
							stepTwoData={stepTwoData}
							data={stepThreeData}
							onChangeData={(data) => {
								setStepThreeData(data);
								handleSubmit(data);
							}}
							toggleNextButtonDisable={(status) => setIsNextButtonDisabled(status)}
						/>
					)}
					{isLoading && <LoadingIndicator size={"s"} />}
				</Box>
				<Flex justifyContent="flex-end" mt="4">
					{renderNextButton()}
				</Flex>
			</Styles>
		</LoggedInLayout>
	);
};

export default JobCreatePage;

const Styles = styled.div`
	width: 100%;
	padding-bottom: 25px;
`;
